import {
  AppWindow,
  Cloud,
  Database,
  Network,
  Server,
  ShieldCheck,
  UsersRound,
} from 'lucide-react'
import { useState } from 'react'

type EnvironmentMode = 'cloud' | 'on-premises' | 'hybrid'

const modes: Array<{
  id: EnvironmentMode
  label: string
  description: string
}> = [
  {
    id: 'cloud',
    label: 'Cloud',
    description:
      'Identity, applications, collaboration and data delivered through well-chosen cloud platforms.',
  },
  {
    id: 'on-premises',
    label: 'On-premises',
    description:
      'Servers, directories, applications, networks and data that remain within the organisation’s environment.',
  },
  {
    id: 'hybrid',
    label: 'Hybrid',
    description:
      'Cloud and on-premises systems designed to work together with clear identity, connectivity and ownership.',
  },
]

export function EnvironmentExplorer() {
  const [mode, setMode] = useState<EnvironmentMode>('hybrid')
  const selected = modes.find((item) => item.id === mode) ?? modes[2]

  return (
    <div className="environment-explorer" data-mode={mode}>
      <div className="environment-switcher" role="tablist" aria-label="Environment type">
        {modes.map((item) => (
          <button
            type="button"
            role="tab"
            aria-selected={mode === item.id}
            key={item.id}
            onClick={() => setMode(item.id)}
          >
            {item.id === 'cloud' && <Cloud aria-hidden="true" strokeWidth={1.2} />}
            {item.id === 'on-premises' && <Server aria-hidden="true" strokeWidth={1.2} />}
            {item.id === 'hybrid' && <Network aria-hidden="true" strokeWidth={1.2} />}
            <span>{item.label}</span>
          </button>
        ))}
        <p>{selected.description}</p>
      </div>
      <div className="environment-diagram" aria-live="polite">
        <svg viewBox="0 0 700 610" aria-hidden="true">
          <path className="line line--identity" d="M350 98V196M350 240V320" />
          <path className="line line--left" d="M350 218H170V390M170 390H275" />
          <path className="line line--right" d="M350 218H530V390M530 390H425" />
          <path className="line line--data" d="M350 365V485" />
          <path className="line line--base" d="M170 445 350 535 530 445" />
        </svg>
        <div className="environment-node environment-node--people">
          <UsersRound aria-hidden="true" />
          <span>People</span>
        </div>
        <div className="environment-node environment-node--identity">
          <ShieldCheck aria-hidden="true" />
          <span>Identity</span>
        </div>
        <div className="environment-node environment-node--apps">
          <AppWindow aria-hidden="true" />
          <span>Applications</span>
        </div>
        <div className="environment-node environment-node--server">
          <Server aria-hidden="true" />
          <span>Servers</span>
        </div>
        <div className="environment-node environment-node--cloud">
          <Cloud aria-hidden="true" />
          <span>Cloud</span>
        </div>
        <div className="environment-node environment-node--data">
          <Database aria-hidden="true" />
          <span>Data</span>
        </div>
        <div className="environment-node environment-node--network">
          <Network aria-hidden="true" />
          <span>Network & devices</span>
        </div>
      </div>
    </div>
  )
}
